import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Header } from '../components/Header';
import { Price } from '../components/Price';
import { Thumb } from '../components/ProductCard';
import { Qty } from '../components/Qty';
import { money, priceAt } from '../format';
import { useShop } from '../shop';
import { cartStore, useCart } from '../store';

export function ProductPage() {
  const { productSlug = '' } = useParams();
  const { slug, shop, catalogue, session } = useShop();
  const navigate = useNavigate();
  const product = useMemo(() => catalogue.products.find((p) => p.slug === productSlug), [catalogue, productSlug]);
  const cart = useCart(slug);

  const [variantId, setVariantId] = useState<number | null>(null);
  const [image, setImage] = useState(0);
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);

  useEffect(() => {
    document.title = product ? `${product.name} · ${shop.name}` : shop.name;
    setImage(0);
    setQty(1);
    setAdded(false);
    setVariantId(null);
  }, [product, shop.name]);

  if (!product) {
    return (
      <>
        <Header />
        <main className="mx-auto max-w-5xl px-4 py-16 text-center text-stone-500">
          That product is no longer listed.
          <div className="mt-4">
            <Link to={`/${slug}`} className="text-[var(--brand)] underline">Back to the shop</Link>
          </div>
        </main>
      </>
    );
  }

  const variants = product.variants;
  const single = variants.length === 1;
  const variant = single ? variants[0]! : (variants.find((v) => v.id === variantId) ?? null);
  const unit = variant ? priceAt(variant.price, variant.breaks, qty) : null;
  const inCart = variant ? (cart.find((l) => l.variantId === variant.id)?.qty ?? 0) : 0;
  const canOrder = !shop.orderingPaused && session.pricesShown;
  const soldOut = variant?.inStock === false;

  function add() {
    if (!variant) return;
    cartStore.add(slug, product!.id, variant.id, qty);
    setAdded(true);
  }

  return (
    <>
      <Header />
      <main className="mx-auto max-w-5xl px-4 pb-28 pt-4">
        <div className="grid gap-5 md:grid-cols-2">
          <div>
            <Thumb src={product.images[image]} alt={product.name} className="aspect-square w-full rounded-2xl" />
            {product.images.length > 1 && (
              <div className="mt-2 flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none]">
                {product.images.map((src, i) => (
                  <button key={src} type="button" onClick={() => setImage(i)} aria-label={`Picture ${i + 1}`} className={`shrink-0 overflow-hidden rounded-lg ring-2 ${i === image ? 'ring-[var(--brand)]' : 'ring-transparent'}`}>
                    <img src={src} alt="" className="size-16 object-cover" loading="lazy" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <h1 className="text-xl font-semibold text-stone-900 dark:text-stone-100">{product.name}</h1>
            {(product.sku || product.unit) && (
              <p className="mt-0.5 text-xs text-stone-500">
                {product.sku && <span>Code {product.sku}</span>}
                {product.sku && product.unit && ' · '}
                {product.unit && <span>per {product.unit}</span>}
              </p>
            )}
            <div className="mt-3 text-2xl">
              <Price value={variant ? unit : product.priceFrom} from={!variant && variants.length > 1} />
              {variant && unit !== null && variant.price !== null && unit < variant.price && (
                <span className="ml-2 align-middle text-sm text-stone-500 line-through">{money(variant.price, shop.currency)}</span>
              )}
            </div>
            {variant?.breaks && variant.breaks.length > 0 && session.pricesShown && (
              <ul className="mt-2 flex flex-wrap gap-1.5 text-xs">
                {variant.breaks.map((b) => (
                  <li key={b.minQty} className={`rounded-full px-2.5 py-1 ring-1 ${qty >= b.minQty ? 'bg-[var(--brand)]/10 text-stone-900 ring-[var(--brand)]/40 dark:text-stone-100' : 'text-stone-600 ring-stone-200 dark:text-stone-300 dark:ring-stone-700'}`}>
                    {b.minQty}+ at {money(b.price, shop.currency)}
                  </li>
                ))}
              </ul>
            )}

            {!single && (
              <fieldset className="mt-5">
                <legend className="mb-2 text-sm font-medium text-stone-700 dark:text-stone-300">Choose an option</legend>
                <div className="flex flex-wrap gap-2">
                  {variants.map((v) => (
                    <button
                      key={v.id}
                      type="button"
                      aria-pressed={variantId === v.id}
                      disabled={v.inStock === false}
                      onClick={() => {
                        setVariantId(v.id);
                        setAdded(false);
                      }}
                      className={`rounded-xl px-3.5 py-2 text-sm ring-1 transition disabled:opacity-40 ${
                        variantId === v.id ? 'bg-[var(--brand)] text-white ring-[var(--brand)]' : 'bg-white text-stone-800 ring-stone-200 dark:bg-stone-900 dark:text-stone-100 dark:ring-stone-700'
                      }`}
                    >
                      <span className="block">{v.name}</span>
                      {session.pricesShown && v.price !== null && <span className={`block text-xs ${variantId === v.id ? 'text-white/80' : 'text-stone-500'}`}>{money(v.price, shop.currency)}</span>}
                    </button>
                  ))}
                </div>
              </fieldset>
            )}

            {variant?.stock !== null && variant?.stock !== undefined && <p className="mt-3 text-sm text-amber-700 dark:text-amber-400">Only {variant.stock} left</p>}
            {soldOut && <p className="mt-3 text-sm text-stone-500">Out of stock right now.</p>}

            {product.description && <p className="mt-5 whitespace-pre-line text-sm leading-relaxed text-stone-600 dark:text-stone-300">{product.description}</p>}
            {variant?.attributes && Object.keys(variant.attributes).length > 0 && (
              <dl className="mt-4 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
                {Object.entries(variant.attributes).map(([k, v]) => (
                  <div key={k} className="contents">
                    <dt className="text-stone-500">{k}</dt>
                    <dd className="text-stone-800 dark:text-stone-200">{v}</dd>
                  </div>
                ))}
              </dl>
            )}
          </div>
        </div>
      </main>

      <div className="fixed inset-x-0 bottom-0 z-20 border-t border-stone-200 bg-white/95 px-4 pb-[max(env(safe-area-inset-bottom),12px)] pt-3 backdrop-blur dark:border-stone-800 dark:bg-stone-950/95">
        <div className="mx-auto flex max-w-5xl items-center gap-3">
          {canOrder && !soldOut ? (
            <>
              <Qty value={qty} onChange={(n) => setQty(Math.max(1, n))} min={1} />
              {added ? (
                <button type="button" onClick={() => navigate(`/${slug}/cart`)} className="h-11 flex-1 rounded-full bg-stone-900 text-sm font-semibold text-white dark:bg-stone-100 dark:text-stone-900">
                  In cart ({inCart}) · View cart
                </button>
              ) : (
                <button type="button" disabled={!variant} onClick={add} className="h-11 flex-1 rounded-full bg-[var(--brand)] text-sm font-semibold text-white disabled:opacity-40">
                  {variant ? (unit !== null ? `Add · ${money(unit * qty, shop.currency)}` : 'Add to cart') : 'Choose an option'}
                </button>
              )}
            </>
          ) : (
            <p className="flex-1 text-center text-sm text-stone-500">
              {shop.orderingPaused ? shop.pauseMessage || 'Not taking orders right now.' : soldOut ? 'Out of stock' : 'Open the shop from your WhatsApp link to order.'}
            </p>
          )}
        </div>
      </div>
    </>
  );
}
